import { useEffect, useState } from "react";
import { Sparkles, Lock, Unlock, RefreshCw, ExternalLink } from "lucide-react";
import api from "../../lib/api";

export default function DevAIUsage() {
  const [d, setD] = useState({});
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { const r = await api.get("/developer/ai-usage"); setD(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const todayPct = d.global_daily_limit ? Math.min(100, (d.global_today / d.global_daily_limit) * 100) : 0;
  const monthPct = d.global_monthly_limit ? Math.min(100, (d.global_month / d.global_monthly_limit) * 100) : 0;
  const aedRate = d.leonardo_token_to_aed_rate || 0.0132;
  const leoLifetime = d.leonardo_lifetime || {};
  const leoUSD = leoLifetime.cost_usd || 0;
  const leoAED = (leoLifetime.credits || 0) * aedRate;

  const StatusBadge = () => (
    <span className={`text-[10px] uppercase tracking-luxe px-2 py-1 flex items-center gap-1 ${d.ai_image_generation_enabled ? "bg-[#32D74B]/10 text-[#32D74B]" : "bg-red-500/10 text-red-400"}`}>
      {d.ai_image_generation_enabled ? <><Unlock size={10}/> Active</> : <><Lock size={10}/> Locked</>}
    </span>
  );

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-end gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">AI Usage</div>
          <h1 className="font-display text-4xl text-white">Image Generation Activity</h1>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <StatusBadge/>
            <span className="text-[11px] uppercase tracking-luxe text-white/50">Provider:</span>
            <span className="text-[11px] uppercase tracking-luxe text-copper" data-testid="active-provider">{d.ai_provider || "gemini"}</span>
            {d.ai_provider === "leonardo" && (
              <span className={`text-[11px] uppercase tracking-luxe ${d.leonardo_configured ? "text-[#32D74B]" : "text-red-400"}`}>
                · Leonardo {d.leonardo_configured ? "configured" : "NOT configured"}
              </span>
            )}
          </div>
        </div>
        <button onClick={refresh} disabled={loading} className="btn-outline text-xs"><RefreshCw size={12}/> Refresh</button>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <div className="card-luxe p-6">
          <div className="overline mb-3">Today</div>
          <div className="font-display text-3xl text-white">{d.global_today || 0}<span className="text-white/30 text-lg">/{d.global_daily_limit || 0}</span></div>
          <div className="h-1 bg-white/5 mt-3"><div className="h-full bg-copper" style={{ width: `${todayPct}%` }}/></div>
        </div>
        <div className="card-luxe p-6">
          <div className="overline mb-3">Month</div>
          <div className="font-display text-3xl text-white">{d.global_month || 0}<span className="text-white/30 text-lg">/{d.global_monthly_limit || 0}</span></div>
          <div className="h-1 bg-white/5 mt-3"><div className="h-full bg-copper" style={{ width: `${monthPct}%` }}/></div>
        </div>
        <div className="card-luxe p-6">
          <div className="overline mb-3">Cost This Month</div>
          <div className="font-display text-3xl text-copper" data-testid="cost-month">${(d.cost_month || 0).toFixed(2)}</div>
          <div className="text-xs text-white/40 mt-1">Today ${(d.cost_today || 0).toFixed(2)}</div>
        </div>
        <div className="card-luxe p-6">
          <div className="overline mb-3">Leonardo Lifetime</div>
          <div className="font-display text-2xl text-copper" data-testid="leo-lifetime-usd">${leoUSD.toFixed(2)}</div>
          <div className="text-xs text-white/40 mt-1">{(leoLifetime.imgs || 0)} images · {(leoLifetime.credits || 0)} tokens · ~AED {leoAED.toFixed(2)}</div>
        </div>
      </div>

      {/* Leonardo account live status */}
      {d.ai_provider === "leonardo" && d.leonardo_account && (
        <div className="card-luxe p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="overline">Leonardo.AI Account</div>
              {d.leonardo_account.ok ? (
                <div className="text-sm mt-2">
                  <div className="text-white">{d.leonardo_account.username}<span className="text-white/40 text-xs ml-2">{d.leonardo_account.plan}</span></div>
                  <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                    <div><div className="text-white/40">Image Tokens</div><div className="text-copper text-2xl font-display">{d.leonardo_account.tokens_remaining}</div></div>
                    <div><div className="text-white/40">GPT Tokens</div><div className="text-copper text-2xl font-display">{d.leonardo_account.gpt_tokens_remaining}</div></div>
                    <div><div className="text-white/40">Model Train</div><div className="text-copper text-2xl font-display">{d.leonardo_account.model_train_tokens_remaining}</div></div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-red-400 mt-2">{d.leonardo_account.error}</div>
              )}
            </div>
            <a href="https://app.leonardo.ai/api-access" target="_blank" rel="noreferrer" className="btn-outline text-xs"><ExternalLink size={12}/> Manage Account</a>
          </div>
        </div>
      )}

      {/* Provider breakdown */}
      <div className="card-luxe p-6">
        <div className="overline mb-4">Provider Breakdown (this month)</div>
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-luxe text-white/40">
            <tr className="border-b border-white/5"><th className="text-left py-2">Provider</th><th className="text-right">Calls</th><th className="text-right">Images</th><th className="text-right">Tokens</th><th className="text-right">Cost (USD)</th></tr>
          </thead>
          <tbody>
            {(d.by_provider || []).map((p) => (
              <tr key={p._id} className="border-b border-white/5">
                <td className="py-3 text-white">{p._id || "(unknown)"}</td>
                <td className="text-right text-white/70">{p.calls || 0}</td>
                <td className="text-right text-white/70">{p.count || 0}</td>
                <td className="text-right text-white/70">{p.credits_used || 0}</td>
                <td className="text-right text-copper">${(p.cost_usd || 0).toFixed(3)}</td>
              </tr>
            ))}
            {(d.by_provider || []).length === 0 && <tr><td colSpan={5} className="py-6 text-center text-white/40">No usage this month</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Recent generations */}
      <div className="card-luxe p-6">
        <div className="overline mb-4">Recent Generations</div>
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-luxe text-white/40">
            <tr className="border-b border-white/5"><th className="text-left py-2">Time</th><th className="text-left">User</th><th className="text-left">Provider</th><th className="text-left">Count</th><th className="text-left">Status</th><th className="text-right">Cost</th></tr>
          </thead>
          <tbody>
            {(d.logs || []).slice(0, 50).map((l) => (
              <tr key={l.id} className="border-b border-white/5" data-testid={`ai-log-${l.id}`}>
                <td className="py-3 text-white/60">{new Date(l.created_at).toLocaleString()}</td>
                <td className="text-white/80">{l.user_email}</td>
                <td className="text-copper uppercase tracking-luxe text-xs">{l.provider || "gemini"}</td>
                <td>{l.count}</td>
                <td className={l.success ? "text-[#32D74B]" : "text-red-400"} title={l.error || ""}>{l.success ? "✓ Success" : "× Failed"}</td>
                <td className="text-right text-copper">${(l.cost_usd || 0).toFixed(3)}</td>
              </tr>
            ))}
            {(d.logs || []).length === 0 && <tr><td colSpan={6} className="py-6 text-center text-white/40">No usage yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
